import {
  consumerSubscribePushNotification,
  consumerUnsubscribeAllPushNotifications,
  consumerUnsubscribePushNotification,
  createDeliveryEvent,
  createEvoClient,
} from "@jti/evo-client";
import type { PushDeliveryEventType } from "@jti/fe-common";
import { getConfig } from "@jti/fe-common";
import type {
  MessagingSubscribePayload,
  MessagingSubscribeResponse,
  MessagingUnsubscribePayload,
} from "@jti/rest-api-types/types/MessagingApi.types";

import { getUser } from "../../utils";
import type { RestResponseType } from "../Rest";

export class MessagingApi {
  private readonly client;
  private static instance: MessagingApi;

  protected constructor(
    baseUrl: string,
    protected tenantToken: string,
    protected recaptchaId: string
  ) {
    const { ciam_token } = getUser();
    this.client = createEvoClient({
      baseUrl,
      throwOnError: false,
      recaptchaId,
      tenantToken,
      ciamToken: ciam_token,
    });
  }

  static init() {
    if (!MessagingApi.instance) {
      const { EVO_DOMAIN, EVO_TENANT, "X-RECAPTCHA-ID": recaptchaId } = getConfig();
      MessagingApi.instance = new MessagingApi(EVO_DOMAIN, EVO_TENANT, recaptchaId);
    }
    return MessagingApi.instance;
  }

  async subscribeUser({ token }: MessagingSubscribePayload): Promise<RestResponseType<MessagingSubscribeResponse>> {
    const { ciam_token } = getUser();

    if (!ciam_token) {
      throw new Error("User is not logged in");
    }

    const { data, error, response } = await consumerSubscribePushNotification({
      client: this.client,
      body: {
        firebase_token: token,
      },
    });

    if (!response.ok) {
      throw { ...error, statusCode: response.status };
    }

    if (!data?.data) {
      throw new Error("Unexpected empty response");
    }

    return { data: data.data, statusCode: response.status };
  }

  async unsubscribe({ subscriptionId }: MessagingUnsubscribePayload): Promise<RestResponseType> {
    const { ciam_token } = getUser();

    if (!ciam_token) {
      throw new Error("User is not logged in");
    }

    const { error, response } = await consumerUnsubscribePushNotification({
      client: this.client,
      path: { subscriptionUuid: subscriptionId },
    });

    if (!response.ok) {
      throw { ...error, statusCode: response.status };
    }

    return { statusCode: response.status };
  }

  async reportDeliveryEvent(
    reference: string,
    eventType: PushDeliveryEventType,
    metadata?: Record<string, unknown>
  ): Promise<void> {
    await createDeliveryEvent({
      client: this.client,
      body: {
        reference,
        event_type: eventType,
        ...(metadata && { raw_payload: metadata }),
      },
    }).catch((error) => {
      console.debug("Failed to report delivery event", error);
    });
  }

  async unsubscribeFromEverything(): Promise<RestResponseType> {
    const { ciam_token } = getUser();

    if (!ciam_token) {
      throw new Error("User is not logged in");
    }

    const { error, response } = await consumerUnsubscribeAllPushNotifications({
      client: this.client,
    });

    if (!response.ok) {
      throw { ...error, statusCode: response.status };
    }

    return { statusCode: response.status };
  }
}
